import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, type RouterHistory } from '@tanstack/react-router'
import { useMemo } from 'react'
import { SessionProvider } from '../auth/session'
import { I18nProvider, initialLocale, readStoredLocale, type Locale } from '../i18n'
import { ToastProvider } from '../ui'
import { ErrorBoundary } from './ErrorBoundary'
import { createQueryClient } from './queryClient'
import { createAppRouter } from './router'
import { ToastDismissLabel } from './ToastDismissLabel'

export interface AppProps {
  readonly locale?: Locale
  /** Test seam: a memory history so the real router can be driven without a
   *  real browser location. Production passes nothing. */
  readonly history?: RouterHistory
}

/**
 * Provider order is not arbitrary:
 *   i18n        — the error boundary's fallback needs to be translatable
 *   query       — SessionProvider is built on useQuery
 *   session     — everything below reads it
 *   toast       — needs translated labels, so it sits under i18n
 *   router      — screens are the innermost thing
 */
export function App({ locale, history }: AppProps) {
  const queryClient = useMemo(() => createQueryClient(), [])
  const router = useMemo(() => createAppRouter(history), [history])
  const resolved =
    locale ?? initialLocale(['en', 'fr'], 'en', readStoredLocale(), navigator.languages ?? ['en'])

  return (
    <I18nProvider initial={resolved}>
      <ErrorBoundary area="app">
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <ToastDismissLabel>
              <RouterProvider router={router} />
            </ToastDismissLabel>
          </SessionProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </I18nProvider>
  )
}

export { ToastProvider }
